import { differenceInMinutes, safeParseISO } from 'shared/util/datetime-fns';
import { formatDistance, formatDuration } from 'shared/util/formatting';
import type { EnrichedFlight, Flight, FlightsResult, HotelOffer, HotelResponse, LegConfig, LegWithData, LegWithRows } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const enrichFlights = (flights: Flight[]): EnrichedFlight[] =>
    flights.map((flight, index, allFlights) => {
        const parsedDepartureTime = safeParseISO(flight.departureTime);
        const parsedArrivalTime = safeParseISO(flight.arrivalTime);
        const prevArrival = index > 0 ? safeParseISO(allFlights[index - 1].arrivalTime) : undefined;

        const stopoverMinutes =
            index > 0 && prevArrival && parsedDepartureTime ? differenceInMinutes(prevArrival, parsedDepartureTime) : undefined;

        return {
            flight,
            index,
            parsedDepartureTime,
            parsedArrivalTime,
            stopoverMinutes,
        };
    });

const buildLegsWithRows = (
    legs: LegConfig[],
    outboundFlightsWithComputed: EnrichedFlight[],
    inboundFlightsWithComputed: EnrichedFlight[],
    hasFlightDetails: boolean,
): LegWithRows[] => {
    const legsWithData: LegWithData[] = legs.map((leg) => {
        const summaryDepartureParsed = safeParseISO(leg.summaryDepartureISO);
        const summaryArrivalParsed = safeParseISO(leg.summaryArrivalISO);

        const detailed = leg.key === 'outbound' ? outboundFlightsWithComputed : inboundFlightsWithComputed;
        const hasDetailed = hasFlightDetails && detailed.length > 0;

        let headingDate: Date;

        if (hasDetailed) {
            const firstDeparture = detailed[0]?.parsedDepartureTime;
            if (!firstDeparture) {
                throw new Error('Expected parsedDepartureTime for first detailed flight segment');
            }
            headingDate = firstDeparture;
        } else {
            if (!summaryDepartureParsed) {
                throw new Error('Expected summaryDepartureParsed for leg');
            }
            headingDate = summaryDepartureParsed;
        }

        return {
            ...leg,
            detailed,
            summaryDepartureParsed,
            summaryArrivalParsed,
            headingDate,
            hasDetailed,
        };
    });

    return legsWithData.map((leg) => {
        const { origin, destination, summaryDuration, summaryDepartureParsed, summaryArrivalParsed, hasDetailed, detailed, stopovers } =
            leg;

        const baseRows: EnrichedFlight[] = hasDetailed
            ? detailed
            : summaryDepartureParsed
              ? [
                    {
                        flight: {
                            originAirport: origin,
                            destinationAirport: destination,
                            flightDuration: summaryDuration ?? undefined,
                            airlineCode: undefined,
                            flightNo: undefined,
                            airline: undefined,
                            departureTime: '',
                            arrivalTime: '',
                        } as Flight,
                        index: 0,
                        parsedDepartureTime: summaryDepartureParsed,
                        parsedArrivalTime: summaryArrivalParsed,
                        stopoverMinutes: undefined,
                    },
                ]
              : [];

        const rowsWithStopoverData: EnrichedFlight[] = baseRows.map((row) => {
            const { index, stopoverMinutes, flight } = row;

            const showDetailedStopoverAlert = hasDetailed && index > 0 && stopoverMinutes != null && stopoverMinutes > 0;
            const showSummaryStopoverAlert = !hasDetailed && index === 0 && stopovers != null && stopovers > 0;
            const showStopoverAlert = showDetailedStopoverAlert || showSummaryStopoverAlert;

            let stopoverText: string | undefined;

            if (showStopoverAlert) {
                if (hasDetailed && stopoverMinutes != null) {
                    stopoverText = `Mellomlanding i ${flight.originAirport} (${formatDuration(stopoverMinutes)})`;
                } else if (!hasDetailed && stopovers != null) {
                    stopoverText = stopovers > 1 ? `${stopovers} mellomlandinger` : `${stopovers} mellomlanding`;
                }
            }

            return {
                ...row,
                showStopoverAlert,
                stopoverText,
            };
        });

        return {
            ...leg,
            rows: rowsWithStopoverData,
        };
    });
};

export const buildFlightsResult = (offer: HotelOffer): FlightsResult => {
    const {
        originAirport,
        destinationAirport,
        departureDate,
        departureDateArrival,
        departureStopovers,
        departureTravelDuration,
        returnDate,
        returnDateArrival,
        returnStopovers,
        returnTravelDuration,
        flightDetails,
    } = offer;

    const hasFlightDetails = Boolean(flightDetails);
    const outboundFlights: Flight[] = flightDetails?.outbound ?? [];
    const inboundFlights: Flight[] = flightDetails?.inbound ?? [];

    const outboundFlightsWithComputed = enrichFlights(outboundFlights);
    const inboundFlightsWithComputed = enrichFlights(inboundFlights);

    const legs: LegConfig[] = [
        {
            key: 'outbound',
            isReturn: false,
            summaryDepartureISO: departureDate,
            summaryArrivalISO: departureDateArrival,
            summaryDuration: departureTravelDuration,
            stopovers: departureStopovers,
            origin: originAirport,
            destination: destinationAirport,
            detailedFlights: outboundFlights,
        },
        {
            key: 'inbound',
            isReturn: true,
            summaryDepartureISO: returnDate,
            summaryArrivalISO: returnDateArrival,
            summaryDuration: returnTravelDuration,
            stopovers: returnStopovers,
            origin: destinationAirport,
            destination: originAirport,
            detailedFlights: inboundFlights,
        },
    ];

    const legsWithRows = buildLegsWithRows(legs, outboundFlightsWithComputed, inboundFlightsWithComputed, hasFlightDetails);
    const showUnknownStopoversWarning = !hasFlightDetails && (departureStopovers === undefined || returnStopovers === undefined);

    return {
        legsWithRows,
        showUnknownStopoversWarning,
        hasFlightDetails,
    };
};

export const getPricePerPerson = (pricePerPerson: number, numPax: number, paxEnabled: boolean, priceTotal?: number) => {
    let calculatedPrice = pricePerPerson;
    if (paxEnabled) {
        if (priceTotal != null) {
            calculatedPrice = Math.round(priceTotal / numPax);
        }
    }
    return calculatedPrice;
};

export const getPriceTotal = (pricePerPerson: number, numPax: number, paxEnabled: boolean, priceTotal?: number) => {
    if (paxEnabled) {
        return priceTotal ?? numPax * pricePerPerson;
    } else {
        return numPax * pricePerPerson;
    }
};

export const getNumberOfPax = (numberOfAdults?: number, numberOfChildren?: number) => {
    if (numberOfAdults == null || numberOfChildren == null) {
        return 2;
    } else {
        return numberOfAdults + numberOfChildren;
    }
};

export const formatDistances = (distanceToBeach?: number, distanceToCity?: number): string =>
    [
        distanceToBeach == null ? null : `${formatDistance(distanceToBeach)} fra strand`,
        distanceToCity == null ? null : `${formatDistance(distanceToCity)} fra sentrum`,
    ]
        .filter(Boolean)
        .join(' • ');

const facilitiesMap = displayValues.amenities.facilities as Record<string, string>;
const activitiesMap = displayValues.amenities.activities as Record<string, string>;

export const getAmenitiesList = (hotel: HotelResponse): string[] => {
    const translatedFacilities = (hotel.facilities ?? []).map((key) => facilitiesMap[key]).filter(Boolean);
    const translatedActivities = (hotel.activities ?? []).map((key) => activitiesMap[key]).filter(Boolean);

    return [...translatedFacilities, ...translatedActivities];
};

export const getAmenitiesText = (hotel: HotelResponse): string => getAmenitiesList(hotel).filter(Boolean).join(' • ');
